import { ReactNode, useState } from 'react';

import { AppIcon, ConfirmModal, EmulationChip } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { CoachingGuideButtons } from '@backrs_ui/grow-ui/src/components/CoachingGuideButtons/CoachingGuideButtons';
import { SandboxContactButton } from '@backrs_ui/grow-ui/src/components/SandboxContactButton/SandboxContactButton';

import { Box, Button, SxProps, TextField, Theme, Typography, useMediaQuery } from '@mui/material';

import { OperatingAsChip } from './OperatingAsChip';

const STRINGS = appStrings.modules.pageShell.header;

export const SHELL_HEADER_COMPACT_QUERY = '(max-width:900px)';

type ShellHeaderProps = Readonly<{
  title: string | null;
  description?: string;
  userFirstName?: string;
  isEmulating?: boolean;
  /** Off by default: this header is also the chat sheet's, where a context control does not belong. */
  showOperatingAs?: boolean;
  onNewChat?: () => void;
  onRestartSession?: () => void;
  onNewOfficeHoursSession?: () => void;
  confirmNewChat?: boolean;
  onSearch?: (searchTerm?: string) => void;
  onGettingStartedClick?: () => void;
  hideGettingStarted?: boolean;
  disabled?: boolean;
  leftPanelWidth?: number;
  isResizing?: boolean;
  isPanelCollapsed?: boolean;
  back?: { label: string; onClick: () => void };
  actionButtons?: ReactNode;
  showSandboxContact?: boolean;
  includeKeyLevers?: boolean;
}>;

export const ShellHeader = ({
  title,
  description,
  userFirstName,
  isEmulating = false,
  showOperatingAs = false,
  onNewChat,
  onRestartSession,
  onNewOfficeHoursSession,
  confirmNewChat = false,
  onSearch,
  onGettingStartedClick,
  hideGettingStarted = false,
  disabled,
  leftPanelWidth = 0,
  isResizing = false,
  isPanelCollapsed = false,
  back,
  actionButtons,
  showSandboxContact = false,
  includeKeyLevers = false,
}: ShellHeaderProps) => {
  const isMobile = useMediaQuery(SHELL_HEADER_COMPACT_QUERY);
  const [searchValue, setSearchValue] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);

  const handleNewChatClick = () => {
    if (confirmNewChat) {
      setConfirmOpen(true);

      return;
    }

    onNewChat?.();
  };

  const defaultTitle = userFirstName ? `Welcome, ${userFirstName}` : STRINGS.defaultTitle;

  const handleSearchSubmit = () => {
    if (onSearch) {
      onSearch(searchValue || undefined);
    }

    setSearchValue('');
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearchSubmit();
    }
  };

  return (
    <Box sx={containerSx}>
      <Box sx={leftSideSx(leftPanelWidth, isResizing, isPanelCollapsed)}>
        <Box sx={titleContainerSx}>
          <Box sx={titleRowSx}>
            <Typography
              variant={isMobile ? 'text-lg-semibold' : 'display-xs-semibold'}
              sx={titleSx}
            >
              {title ?? defaultTitle}
            </Typography>
            {isEmulating && <EmulationChip />}
            {showOperatingAs && <OperatingAsChip id="shell-header-operating-as" />}
          </Box>
          <Typography variant={isMobile ? 'text-sm-regular' : 'text-md-regular'} sx={subtitleSx}>
            {description ?? STRINGS.description}
          </Typography>
        </Box>
        {onNewChat && (
          <Button
            id="shell-new-chat-button"
            hierarchy="Primary"
            size={isMobile ? 'sm' : 'md'}
            startIcon={<AppIcon iconName="Plus" diameter="20px" color="currentColor" />}
            onClick={handleNewChatClick}
            disabled={disabled}
          >
            {STRINGS.newChatButtonShort}
          </Button>
        )}
      </Box>

      <ConfirmModal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        variant="warning"
        headerText={STRINGS.confirmNewChat.header}
        bodyText={STRINGS.confirmNewChat.body}
        primaryAction={{
          id: 'shell-new-office-hours-button',
          label: STRINGS.confirmNewChat.newChat,
          onClick: () => {
            setConfirmOpen(false);
            onNewOfficeHoursSession?.();
          },
        }}
        secondaryAction={{
          id: 'shell-restart-session-button',
          label: STRINGS.confirmNewChat.restart,
          onClick: () => {
            setConfirmOpen(false);
            onRestartSession?.();
          },
        }}
      />

      {!isMobile && (
        <Box sx={rightSideSx}>
          <TextField
            id="shell-header-search"
            variant="outlined"
            size="small"
            placeholder={STRINGS.searchPlaceholder}
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            onKeyDown={handleSearchKeyDown}
            sx={searchFieldSx}
            InputProps={{
              startAdornment: (
                <Box sx={searchAdornmentSx}>
                  <AppIcon
                    iconName="SearchLg"
                    diameter="20px"
                    color="Colors/Foreground/fg-tertiary"
                  />
                </Box>
              ),
            }}
          />
          <CoachingGuideButtons idPrefix="shell" includeKeyLevers={includeKeyLevers} />
          {showSandboxContact && <SandboxContactButton />}
          {!hideGettingStarted && (
            <Button
              id="shell-getting-started-button"
              hierarchy="Secondary color"
              size="md"
              sx={noWrapSx}
              onClick={onGettingStartedClick}
            >
              {STRINGS.gettingStartedButton}
            </Button>
          )}
          {actionButtons}
          {back && (
            <Button
              id="shell-header-back-btn"
              hierarchy="Secondary color"
              size="md"
              sx={noWrapSx}
              onClick={back.onClick}
              startIcon={<AppIcon iconName="ArrowLeft" diameter="20px" color="currentColor" />}
            >
              {back.label}
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
};

const containerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: `calc(${backrs.spacing['spacing-3xl']} + ${backrs.spacing['spacing-3xl']} + ${backrs.spacing['spacing-md']})`,
  width: '100%',
  pl: { xs: backrs.spacing['spacing-lg'], md: backrs.spacing['spacing-2xl'] },
  pt: { xs: backrs.spacing['spacing-md'], md: backrs.spacing['spacing-2xl'] },
  pb: { xs: backrs.spacing['spacing-md'], md: backrs.spacing['spacing-3xl'] },
  flexShrink: 0,
  borderBottom: {
    xs: `1px solid ${backrs.colors['Colors/Border/border-secondary']}`,
    md: 'none',
  },
});

const leftSideSx =
  (leftPanelWidth: number, isResizing: boolean, isPanelCollapsed: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: backrs.spacing['spacing-lg'],
    width: { xs: '100%', md: isPanelCollapsed ? 'auto' : `${leftPanelWidth}%` },
    minWidth: 'unset',
    flexShrink: 0,
    pr: { xs: backrs.spacing['spacing-lg'], md: 0 },
    ...(isResizing ? {} : { transition: 'width 0.3s ease-in-out' }),
  });

const titleContainerSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  minWidth: 0,
};

const titleRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: backrs.spacing['spacing-lg'],
  minWidth: 0,
});

const titleSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-primary'],
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

const subtitleSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

const rightSideSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-md'],
  flex: 1,
  justifyContent: 'flex-end',
  pr: backrs.spacing['spacing-2xl'],
});

const searchAdornmentSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  minWidth: '20px',
  minHeight: '20px',
};

const noWrapSx: SxProps<Theme> = { whiteSpace: 'nowrap' };

const searchFieldSx: SxProps<Theme> = ({ backrs }) => ({
  maxWidth: '220px',
  minWidth: '100px',
  flex: 1,
  '& .MuiOutlinedInput-root': {
    borderRadius: backrs.spacing['spacing-md'],
    backgroundColor: backrs.colors['Colors/Background/bg-primary'],
    '& fieldset': {
      borderColor: backrs.colors['Colors/Border/border-primary'],
    },
    '&:hover fieldset': {
      borderColor: backrs.colors['Colors/Border/border-secondary'],
    },
  },
  '& .MuiInputBase-input': {
    padding: `${backrs.spacing['spacing-sm']} ${backrs.spacing['spacing-md']}`,
    paddingLeft: 0,
  },
});
